import type { components } from "@contracts/generated/editor-service";

import { splitPartKey, type DraftPart } from "./assetContent";
import { joinPartKey, namedRows, type NamedDocument } from "./namedParts";

/**
 * A Rule's content, projected the way the canonical Rules editor shows it
 * (#910).
 *
 * WHAT THE CANONICAL REFERENCE HAS AND THIS REPOSITORY DOES NOT. The reference
 * addresses one language at a time — `/library/rules/english` is a page whose
 * heading is "English" and whose body is one readable prompt field. This
 * repository's Rule is ONE BILINGUAL ASSET: `placement_registry` requires both
 * `he` and `en` on every version, one adoption pins the pair, and the pack files
 * they compose to are `conversation_agent/{lang}/…`. So the reference's
 * per-language page becomes a per-language VIEW of one asset, reached from a
 * list of the languages that asset carries, and the asset's identity — its id,
 * its version, its adoption count — is the same on both. Splitting it into two
 * assets to match the mock's addressing would break the invariant the contract
 * names in the same sentence as the mock.
 *
 * WHAT THIS MODULE MAY KNOW. Roles are the SERVICE's vocabulary, read off
 * `namedParts` and the part manifest; nothing here enumerates which roles a Rule
 * has. What is here is the per-language grouping, product copy for the languages
 * and roles a Rule actually carries, and the pure edit operations the tables
 * need — the same discipline `namedParts` states at its own top, applied one
 * axis over.
 *
 * WHAT IT DELIBERATELY DOES NOT KNOW: the composition of an authored document
 * with base canon. That is the SERVICE's `RulesSubstitutionProjection`, proved
 * against the pinned provider by
 * `test_rules_substitutions_against_real_brain_checkout.py`. This module applies
 * the clinician's own IN-FLIGHT edits on top of the base map the service
 * published — which is the one part of the composition no server can answer,
 * because an unsaved draft does not exist anywhere but this browser — and the
 * moment a version is published the service's own answer replaces it.
 */

export type RulesSubstitutionProjection =
  components["schemas"]["RulesSubstitutionProjection"];
export type SubstitutionLanguage =
  components["schemas"]["SubstitutionLanguage"];
export type SubstitutionBucketName =
  components["schemas"]["SubstitutionBucket"]["bucket"];
export type SubstitutionGenderForm =
  components["schemas"]["SubstitutionGenderForm"];

/**
 * Product copy for the languages a Rule carries.
 *
 * PARTIAL BY CONSTRUCTION, with the tag itself behind it, on
 * `namedParts.ROLE_PRODUCT_NAMES`' rule: only entries whose product name
 * genuinely differs from the tag are worth stating, and a language the pack
 * gains tomorrow renders as its own tag rather than as a blank row. The two
 * here are the two `BILINGUAL_RULE_LANGUAGES` names, and they are the names the
 * canonical reference itself puts on those pages.
 */
const LANGUAGE_PRODUCT_NAMES: Record<string, string> = {
  he: "Hebrew",
  en: "English",
};

/** `he` -> `Hebrew`; an unmapped tag reads as itself. */
export function languageProductName(language: string): string {
  return LANGUAGE_PRODUCT_NAMES[language] ?? language;
}

/**
 * Product copy for the four substitution tables.
 *
 * The bucket NAMES are the provider's (`filesystem_provider._SUBSTITUTION_GENDER_BUCKETS`
 * plus `common`) and arrive from the service on every row; these are the
 * sentences that say what each one does, which is the half a bucket name does
 * not carry. `other` in particular is not "other genders": the provider reads it
 * for a genderform it does not recognise AND for an absent one, so a clinician
 * who read it as a third gender would leave the most common fallback unwritten.
 */
export const BUCKET_LABELS: Record<SubstitutionBucketName, string> = {
  common: "Every patient",
  female: "Female form",
  male: "Male form",
  other: "Other or unknown form",
};

export const BUCKET_HINTS: Record<SubstitutionBucketName, string> = {
  common:
    "Applied to every reply, whatever the patient's recorded gender form. A gendered table below can override any of these.",
  female: "Applied instead of the shared entry when the patient's gender form is female.",
  male: "Applied instead of the shared entry when the patient's gender form is male.",
  other:
    "Applied when the gender form is recorded as other — and when none is recorded at all, which is what an unrecognised value falls back to.",
};

/** Where a row's replacement comes from, as the surface renders it. */
export type SubstitutionRowOrigin = "base" | "authored" | "inherited";

export interface SubstitutionEditorRow {
  /** The literal text the runtime matches. Stable identity within a bucket. */
  find: string;
  /** The literal text it is replaced with. `""` is a real replacement. */
  replacement: string;
  origin: SubstitutionRowOrigin;
  /**
   * What base replaces this find text with, or `null` when base does not carry
   * it at all.
   *
   * The whole of what Reset needs: non-null means removing the authored entry
   * restores THIS value, and null means removing it removes the row.
   */
  inheritedReplacement: string | null;
}

export interface SubstitutionBucketView {
  bucket: SubstitutionBucketName;
  label: string;
  hint: string;
  /** Whether the draft's own document declares this bucket. */
  authored: boolean;
  rows: SubstitutionEditorRow[];
}

/** One bucket of an authored document: find text -> replacement. */
export type SubstitutionTable = Record<string, string>;
/** An authored `substitutions` document, as the draft part holds it. */
export type SubstitutionDocument = Partial<
  Record<SubstitutionBucketName, SubstitutionTable>
>;

export const SUBSTITUTION_BUCKET_ORDER: SubstitutionBucketName[] = [
  "common",
  "female",
  "male",
  "other",
];

/**
 * The `substitutions` part for one language, or `null`.
 *
 * Located by ROLE and language prefix, never by position: `parts` is an ordered
 * list and a Rule's roles are not adjacent per language (the seed sorts by key),
 * so an index would address a different document on a differently ordered asset.
 */
export function findSubstitutionPart(
  parts: readonly DraftPart[],
  language: string,
): DraftPart | null {
  return (
    parts.find((part) => {
      const [partLanguage, role] = splitPartKey(part.key);
      return partLanguage === language && role === "substitutions";
    }) ?? null
  );
}

/**
 * The draft part's buffer as a document, or `null` when it is not one yet.
 *
 * `null` rather than `{}` for unparseable or non-object JSON, so a caller can
 * tell "this document says nothing" from "this buffer is mid-edit or was
 * authored elsewhere as something else". The tables refuse to render over the
 * second case rather than silently presenting an empty document — the server's
 * own refusal, quoted at Save, stays the authority on what is publishable.
 */
export function readSubstitutionDocument(
  part: DraftPart | null,
): SubstitutionDocument | null {
  if (part === null) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(part.text);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return null;
  }
  // A NAME OUTSIDE THE FOUR BUCKETS MAKES THE WHOLE DOCUMENT UNREADABLE HERE,
  // and reading past it was a silent data loss rather than tolerance. This
  // projection is re-serialised on the next edit, so a bucket this loop skipped
  // would be DROPPED from the published document — and the result would then
  // pass the service's own gate, because the name it refuses is exactly the one
  // that went missing. The service refuses such a document (`extra="forbid"`)
  // and reports the language unreadable; answering `null` here is the same
  // answer, reached without waiting for that read.
  const names = Object.keys(parsed as Record<string, unknown>);
  if (names.some((name) => !SUBSTITUTION_BUCKET_ORDER.includes(name as SubstitutionBucketName))) {
    return null;
  }
  const document: SubstitutionDocument = {};
  for (const bucket of SUBSTITUTION_BUCKET_ORDER) {
    const table = (parsed as Record<string, unknown>)[bucket];
    if (table === undefined) {
      // ABSENT AND EMPTY STAY DIFFERENT. Under the engine's deep merge an
      // omitted bucket inherits and `{}` declares an empty table, so a default
      // here would author a document the clinician did not write.
      continue;
    }
    if (table === null || typeof table !== "object" || Array.isArray(table)) {
      return null;
    }
    const pairs = Object.entries(table as Record<string, unknown>);
    if (pairs.some(([, replacement]) => typeof replacement !== "string")) {
      return null;
    }
    // `Object.fromEntries`, NEVER `entries[find] = replacement`. A find text is
    // whatever the clinician typed, and the service accepts every non-empty
    // string — including `__proto__`, which as an assignment target invokes
    // JavaScript's legacy prototype setter instead of creating an own property.
    // The entry then vanished from the rebuilt document, disappeared from the
    // table, and was dropped by the next Save. `fromEntries` creates own
    // properties for every key, which is the same thing `JSON.parse` already did
    // on the way in.
    document[bucket] = Object.fromEntries(pairs) as SubstitutionTable;
  }
  return document;
}

/**
 * The tables to render: the service's composed rows, with the draft applied.
 *
 * THE BASE MAP IS THE SERVICE'S AND THE OVERLAY IS THE DRAFT'S, and the split
 * is the whole reason this is safe. `projection` was composed by the server
 * against base canon and is pinned to the real provider by a Python test; what
 * it cannot know is the unsaved edit in this browser. So base is read OUT of the
 * projection — `inheritedReplacement` where base carries the find text, the row's
 * own replacement where the row is inherited or base — and the draft document is
 * layered on exactly as `dict.update` does, which is the one level the engine's
 * `merge_structured` recursion reaches for this document.
 *
 * A PROJECTION FROM A DIFFERENT VERSION IS STILL USABLE FOR BASE. It is fetched
 * at the version the draft was opened at and base canon does not move under it;
 * if a bucket is missing from it entirely — a language the projection has not
 * seen — the draft's own entries still render, marked authored, which is what
 * they are.
 */
export function substitutionBuckets(
  projection: SubstitutionLanguage | null,
  document: SubstitutionDocument | null,
  /**
   * Whether the version on screen IS base canon's own rule.
   *
   * PASSED, NOT SNIFFED, and the difference is a defect this signature exists to
   * make impossible. On base canon's asset the version's own document sits on
   * the BASE side of the service's projection — so the draft holds the same two
   * hundred entries the projection reports as `base`, and layering it as an
   * overlay would find every one of them "overridden" and label the base library
   * "Changed here". It is the service's `inheritance` field, which is the only
   * thing that actually knows.
   */
  { isBase }: { isBase: boolean } = { isBase: false },
): SubstitutionBucketView[] {
  // The draft is an OVERLAY on base, and on base canon itself there is nothing
  // to overlay: the document on screen is base.
  const overlay = isBase ? null : document;
  return SUBSTITUTION_BUCKET_ORDER.flatMap((bucket) => {
    const published = projection?.buckets.find(
      (entry) => entry.bucket === bucket,
    );
    const base = new Map<string, string>();
    for (const row of published?.rows ?? []) {
      // `undefined` alongside `null` for the reason `licensedInventoryVerdict`
      // states: browser and service deployments are not atomic, and the field is
      // optional in the generated contract because it carries a server default.
      // Both spellings mean the same thing here — base does not carry this find
      // text — and an `undefined` read as a replacement would put the string
      // "undefined" into a clinical table.
      const inherited = row.inheritedReplacement;
      if (inherited !== null && inherited !== undefined) {
        base.set(row.find, inherited);
      } else if (row.origin !== "authored") {
        base.set(row.find, row.replacement);
      }
    }
    const authored = overlay?.[bucket];

    if (published === undefined && authored === undefined) {
      // Neither the service nor the draft says anything about this table.
      // Rendering an empty one would claim the runtime has no entries for it.
      return [];
    }

    const rows: SubstitutionEditorRow[] = [];
    for (const [find, baseReplacement] of base) {
      // `Object.hasOwn`, not `authored?.[find] !== undefined`. A base row whose
      // find text is `__proto__` reads back `Object.prototype` from a document
      // that does not carry it — an object rather than `undefined` — and the row
      // would have been reported as an override of a value nobody authored.
      const override =
        authored !== undefined && Object.hasOwn(authored, find)
          ? authored[find]
          : undefined;
      rows.push(
        override === undefined
          ? {
              find,
              replacement: baseReplacement,
              origin: isBase ? "base" : "inherited",
              inheritedReplacement: baseReplacement,
            }
          : {
              find,
              replacement: override,
              origin: "authored",
              inheritedReplacement: baseReplacement,
            },
      );
    }
    for (const [find, replacement] of Object.entries(authored ?? {})) {
      if (base.has(find)) {
        continue;
      }
      rows.push({
        find,
        replacement,
        origin: "authored",
        inheritedReplacement: null,
      });
    }

    return [
      {
        bucket,
        label: BUCKET_LABELS[bucket],
        hint: BUCKET_HINTS[bucket],
        authored: authored !== undefined,
        rows,
      },
    ];
  });
}

/** How many rows of a bucket this document authors. */
export function authoredRowCount(
  buckets: readonly SubstitutionBucketView[],
): number {
  return buckets.reduce(
    (total, bucket) =>
      total + bucket.rows.filter((row) => row.origin === "authored").length,
    0,
  );
}

/**
 * One authored entry written into the document, and the part's buffer with it.
 *
 * `null` as the replacement REMOVES the authored entry, which is the reset: the
 * base key stays in base, the runtime keeps applying it, and the document simply
 * stops mentioning it. That is the contract's own reading — "removal from an
 * authored override restores inheritance and is not a deletion of base" — and
 * it is why there is no delete operation here that writes anything.
 *
 * A bucket that becomes empty stays DECLARED as `{}` rather than being dropped:
 * `{}` and absence are different documents under deep merge, and silently
 * turning one into the other on the last removal would be an edit the clinician
 * did not make. The bucket a caller never touched is never created.
 */
export function setSubstitution(
  document: SubstitutionDocument | null,
  bucket: SubstitutionBucketName,
  find: string,
  replacement: string | null,
): SubstitutionDocument {
  const next: SubstitutionDocument = { ...(document ?? {}) };
  // Rebuilt through entries for `readSubstitutionDocument`'s reason: assigning
  // or deleting `obj[find]` is unsafe when the find text is `__proto__`, and a
  // find text is whatever the clinician typed. The spread above is safe — spread
  // creates own properties — but the write is not, so the write goes through
  // `fromEntries` too.
  const pairs = Object.entries(next[bucket] ?? {}).filter(
    ([existing]) => existing !== find,
  );
  if (replacement !== null) {
    pairs.push([find, replacement]);
  }
  next[bucket] = Object.fromEntries(pairs);
  return next;
}

/**
 * The document written back into the draft's `substitutions` part.
 *
 * Pretty-printed for `toDraft`'s reason: this buffer is also what the generic
 * parts editor would show, and re-serialising on save drops the indentation
 * again, so the canonical form the server hashes is unaffected.
 *
 * The part is CREATED when the language has none, with its language-prefixed
 * key. That is not the free-text key control the 2026-08-25 amendment forbids:
 * the language is one the asset already carries and the role is the service's,
 * so nothing here is a vocabulary a clinician typed.
 */
export function writeSubstitutionDocument(
  parts: readonly DraftPart[],
  language: string,
  document: SubstitutionDocument,
  nextId: () => string,
): DraftPart[] {
  const text = JSON.stringify(document, null, 2);
  const existing = findSubstitutionPart(parts, language);
  if (existing !== null) {
    return parts.map((part) =>
      part.id === existing.id ? { ...part, text } : part,
    );
  }
  return [
    ...parts,
    {
      id: nextId(),
      key: joinPartKey(language, "substitutions"),
      kind: "structured" as const,
      text,
    },
  ];
}

/** One language's named documents, keyed by role, for the language view. */
export interface RulesLanguageView {
  language: string;
  label: string;
  /** Prompt documents this language carries, in the asset's own role order. */
  prompts: { role: string; label: string; document: NamedDocument }[];
  /** Structured documents other than `substitutions`, in the same order. */
  structured: { role: string; label: string; document: NamedDocument }[];
  /** Whether this language carries a `substitutions` document of its own. */
  hasSubstitutions: boolean;
}

/**
 * The languages this Rule carries, each with its named documents.
 *
 * Built off `namedParts.namedRows`, which is the module that already knows how
 * to read a role out of a part key, so a Rule and a main concern agree on what a
 * role is. `substitutions` is pulled OUT of the document lists deliberately: it
 * has a named table of its own on this surface, and leaving it in the structured
 * list would render the same document twice — once as a table and once as "not
 * authored in this editor".
 */
export function rulesLanguages(
  parts: readonly DraftPart[],
  assetType: string,
): RulesLanguageView[] {
  const languages: string[] = [];
  for (const part of parts) {
    const [language] = splitPartKey(part.key);
    if (language !== null && !languages.includes(language)) {
      languages.push(language);
    }
  }
  const rows = namedRows(parts, assetType);
  return languages.map((language) => {
    const view: RulesLanguageView = {
      language,
      label: languageProductName(language),
      prompts: [],
      structured: [],
      hasSubstitutions: false,
    };
    for (const row of rows) {
      const document = row.documents.find(
        (entry) => entry.language === language,
      );
      if (document === undefined) {
        continue;
      }
      if (row.role === "substitutions") {
        view.hasSubstitutions = true;
        continue;
      }
      const target = row.kind === "prompt_text" ? view.prompts : view.structured;
      target.push({ role: row.role, label: row.label, document });
    }
    return view;
  });
}

/**
 * The copied parts, with the documents that DEEP-MERGE onto base left behind.
 *
 * WHY A COPY IS NOT A DUPLICATE. A Rule's prompt documents REPLACE their base
 * file, so a copy that dropped them would publish a rule with no rules text. Its
 * `substitutions` and `gender_instructions` documents do the opposite: they
 * merge onto base's, so copying them verbatim writes four hundred entries that
 * say exactly what base already says. The runtime result is identical, and
 * everything else about it is worse — the export carries hundreds of redundant
 * lines instead of the handful a project actually changes, every row on the
 * surface reads "Changed here", and "restore the inherited value" becomes a
 * control with nothing to restore. The S7 contract states the rule directly:
 * unchanged base content is inherited and compared, not copied into redundant
 * assets.
 *
 * So a copy carries the prompts and starts the merging documents INHERITED, and
 * the clinician authors the entries they actually change.
 *
 * `inheritingRoles` IS THE SERVICE'S ANSWER — `inheritingRolesFor` off the
 * placement registry — and an empty list means the registry has not said, which
 * is indistinguishable here from "no document merges". THE CALLER MUST NOT COPY
 * IN THAT WINDOW: a verbatim copy is not the fail-safe direction it looks like,
 * because it publishes base canon's whole substitutions table into shared
 * clinical content that every adopting project then exports, as an immutable
 * version. `NewRulesDialog` treats a resolved registry as a precondition and
 * refuses to create without one.
 */
export function copyableParts(
  parts: readonly DraftPart[],
  inheritingRoles: readonly string[],
): DraftPart[] {
  if (inheritingRoles.length === 0) {
    return [...parts];
  }
  return parts.filter(
    (part) => !inheritingRoles.includes(splitPartKey(part.key)[1]),
  );
}

/** One language's slice of the service's projection, or `null`. */
export function projectionFor(
  projection: RulesSubstitutionProjection | undefined,
  language: string,
): SubstitutionLanguage | null {
  return (
    projection?.languages.find((entry) => entry.language === language) ?? null
  );
}
